import useOrgDefaults from '@/src/Organizations/hooks/useOrgDefaults';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import { useEffect, useState } from 'react';

export default function useOrgBranding() {
    const { orgId } = useUserOrg();
    const { getOrgDefaults, updateOrgDefaults, isLoading, error } =
        useOrgDefaults();

    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [lastSaved] = useState<Date | null>(null);
    const [lastChangesMade, setLastChangesMade] = useState<Date | null>(null);
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [emailSubject, setEmailSubject] = useState<string>('');
    const [emailBody, setEmailBody] = useState<string>('');
    const [emailFromName, setEmailFromName] = useState<string>('');
    const [textSignature, setTextSignature] = useState<string>('');
    const [galleryHeaderBackgroundColor, setGalleryHeaderBackgroundColor] =
        useState<string>('#000000');
    const [galleryHeaderForegroundColor, setGalleryHeaderForegroundColor] =
        useState<string>('#ffffff');
    const [galleryBackgroundColor, setGalleryBackgroundColor] =
        useState<string>('#000000');
    const [galleryForegroundColor, setGalleryForegroundColor] =
        useState<string>('#ffffff');
    const [wasLoaded, setWasLoaded] = useState<boolean>(false);

    useEffect(() => {
        const loadDefaults = async () => {
            const defaults = await getOrgDefaults(orgId);
            if (defaults) {
                console.log('defaults', defaults);
                setLogoUrl(defaults.logoUrl);
                setEmailSubject(defaults.emailSubject || '');
                setEmailBody(defaults.emailBody || '');
                setEmailFromName(defaults.emailFromName || '');
                setTextSignature(defaults.textSignature || '');
                setGalleryHeaderBackgroundColor(
                    defaults.galleryHeaderBackgroundColor || '#000000',
                );
                setGalleryHeaderForegroundColor(
                    defaults.galleryHeaderForegroundColor || '#ffffff',
                );
                setGalleryBackgroundColor(
                    defaults.galleryBackgroundColor || '#000000',
                );
                setGalleryForegroundColor(
                    defaults.galleryForegroundColor || '#ffffff',
                );
                setWasLoaded(true);
            }
        };

        loadDefaults();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const saveBranding = async () => {
        if (!wasLoaded) {
            return;
        }
        console.log('saveBranding');
        setIsSaving(true);
        try {
            await updateOrgDefaults(orgId, {
                logoUrl,
                emailSubject,
                emailBody,
                emailFromName,
                textSignature,
                galleryHeaderBackgroundColor,
                galleryHeaderForegroundColor,
                galleryBackgroundColor,
                galleryForegroundColor,
            });
        } catch (error) {
            console.error('Error updating org defaults', error);
        } finally {
            setIsSaving(false);
            setLastChangesMade(new Date());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    };

    useDebouncedEffect(
        saveBranding,
        [
            logoUrl,
            emailSubject,
            emailBody,
            emailFromName,
            textSignature,
            galleryHeaderBackgroundColor,
            galleryHeaderForegroundColor,
            galleryBackgroundColor,
            galleryForegroundColor,
        ],
        10000,
    );

    return {
        isSaving,
        lastSaved,
        error,
        logoUrl,
        lastChangesMade,
        emailSubject,
        emailBody,
        emailFromName,
        textSignature,
        galleryHeaderBackgroundColor,
        galleryHeaderForegroundColor,
        galleryBackgroundColor,
        galleryForegroundColor,
        setGalleryHeaderBackgroundColor,
        setGalleryHeaderForegroundColor,
        setGalleryBackgroundColor,
        setGalleryForegroundColor,
        setLogoUrl,
        setEmailSubject,
        setEmailBody,
        setEmailFromName,
        setTextSignature,
        isLoading,
    };
}

function useDebouncedEffect(fn: () => void, deps: any[], delay: number) {
    useEffect(() => {
        const handler = setTimeout(() => fn(), delay);
        return () => {
            clearTimeout(handler);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}
